import { yupResolver } from '@hookform/resolvers/yup';
import { Save as SaveIcon, Visibility, VisibilityOff } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Container,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import PaperSection from '../../../components/PaperSection';
import { apiClient } from '../../../services/apiClient';
import PasswordField from '../../auth/components/PasswordField';
const resetPasswordSchema = yup.object().shape({
  currentPassword: yup.string().required('Current password is required'),
});

type ResetPasswordFormData = {
  currentPassword: string;
};

export const ResetPassword: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showValidation, setShowValidation] = useState(true);
  const [isPasswordValid, setIsPasswordValid] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ResetPasswordFormData>({
    resolver: yupResolver(resetPasswordSchema),
  });

  const handlePasswordValidation = (isValid: boolean) => {
    setIsPasswordValid(isValid);
  };

  const onSubmit = async (data: ResetPasswordFormData) => {
    setError('');
    setIsSubmitting(true);

    if (!isPasswordValid) {
      setError('Please ensure your new password meets all requirements and passwords match');
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await apiClient.put('/user/reset-password', {
        currentPassword: data.currentPassword,
        newPassword: newPassword,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update password');
      }

      await response.json();

      reset();
      setNewPassword('');
      setConfirmPassword('');
      setIsPasswordValid(false);
      setShowValidation(false);

      enqueueSnackbar('Password updated successfully!', { variant: 'success' });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to update password. Please try again.';
      setError(errorMessage);
      console.error('Password update error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    reset();
    setNewPassword('');
    setConfirmPassword('');
    setIsPasswordValid(false);
    setError('');
  };

  const isDirty = newPassword || confirmPassword || Object.keys(errors).length > 0;

  return (
    <PaperSection title="Change Password">
      <Container maxWidth="xs" sx={{ ml: 0, mr: 'auto', textAlign: 'left' }}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={3}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {/* Current Password */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Current Password
              </Typography>
              <TextField
                fullWidth
                type={showCurrentPassword ? 'text' : 'password'}
                placeholder="Enter your current password"
                {...register('currentPassword')}
                error={!!errors.currentPassword}
                helperText={errors.currentPassword?.message}
                disabled={isSubmitting}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        edge="end"
                        disabled={isSubmitting}
                      >
                        {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                <Link href="/forgot-password" color="primary" underline="hover">
                  Forgot your password?
                </Link>
              </Typography>
            </Box>

            {/* New Password */}
            <Box>
              <Typography variant="subtitle2" gutterBottom sx={{ mb: 2 }}>
                New Password
              </Typography>
              <PasswordField
                onPasswordChange={setNewPassword}
                onConfirmPasswordChange={setConfirmPassword}
                onValidationChange={handlePasswordValidation}
                showConfirmField={true}
                showValidation={showValidation}
                disabled={isSubmitting}
                margin="none"
              />
            </Box>

            {/* Action Buttons */}
            <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ pt: 2 }}>
              {isDirty && (
                <Button variant="outlined" onClick={handleCancel} disabled={isSubmitting}>
                  Cancel
                </Button>
              )}
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={isSubmitting || !isPasswordValid || !newPassword}
                color="primary"
              >
                {isSubmitting ? 'Updating...' : 'Update Password'}
              </Button>
            </Stack>
          </Stack>
        </form>
      </Container>
    </PaperSection>
  );
};

export default ResetPassword;
